import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { PLAN_MARKETING_COPY, hasRole } from '@court-sync/shared';
import { Button, Card, CardContent } from '@/components/ui';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthStore } from '@/store/auth';

const LESSON_PROFESSIONAL_FEATURE_BULLETS = [
  'Coach self-attendance from My Attendance, My Schedule, and Today\'s Roster',
  'Low enrolment alerts with cancel or postpone actions on the dashboard',
  'Make-up lesson credits when students report absences in time',
  'Waitlists for full lessons with automatic spot notifications',
  'Student-initiated private bookings and pro-rata term pricing',
  'SMS for urgent cancellations, postponements, and waitlist alerts',
  'Automated emails when lessons are cancelled or postponed',
] as const;

export function LessonProfessionalFeaturesLockedCard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plans: availablePlans } = useSubscription();
  const isDelegatedAdminOnly = !!(
    user && hasRole(user, 'academy_admin') && !hasRole(user, 'academy_owner')
  );
  const planName = PLAN_MARKETING_COPY.professional.name;

  const professionalPlan = useMemo(() => {
    if (!availablePlans?.length) return null;
    type PlanWithInternalId = { internalId?: string };
    return (
      (availablePlans as (typeof availablePlans[number] & PlanWithInternalId)[]).find(
        (plan) => plan.internalId?.toLowerCase() === 'professional'
      ) ?? null
    );
  }, [availablePlans]);

  return (
    <Card className="border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
      <CardContent className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center">
              <BookOpen
                className="mr-3 h-8 w-8 shrink-0 text-blue-600 dark:text-blue-400"
                aria-hidden
              />
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200">
                Enable Professional lesson settings
              </h3>
            </div>
            <p className="mb-3 text-blue-800 dark:text-blue-300">
              Upgrade to <strong>{planName}</strong> to unlock coach portals, enrolment management,
              make-up credits, waitlists, private bookings, SMS, and automated lesson notifications.
            </p>
            <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-400">
              {LESSON_PROFESSIONAL_FEATURE_BULLETS.map((bullet) => (
                <li key={bullet}>✓ {bullet}</li>
              ))}
            </ul>
            {isDelegatedAdminOnly ? (
              <p className="mt-4 text-sm text-blue-800 dark:text-blue-300">
                The academy owner can upgrade the plan to unlock these settings.
              </p>
            ) : null}
          </div>
          {!isDelegatedAdminOnly && professionalPlan ? (
            <div className="flex shrink-0 sm:pt-1">
              <Button
                onClick={() => navigate('/settings?tab=subscription')}
                className="w-full whitespace-nowrap bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 sm:w-auto"
              >
                Upgrade to {planName}
              </Button>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
